import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, HeartHandshake, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

/**
 * Benefits dialog (admin/manager, from the employee card). Set the annual
 * health-benefit allowance, see what's been used / is left this year, and
 * record pre-app (legacy) claims so old claims count against the balance.
 */
const BenefitsDialog = ({ employee, onClose, testid = "benefits" }) => {
  const [data, setData] = useState(null);
  const [allowance, setAllowance] = useState("");
  const [busy, setBusy] = useState(false);
  const [cDate, setCDate] = useState("");
  const [cDesc, setCDesc] = useState("");
  const [cAmount, setCAmount] = useState("");
  const [cNote, setCNote] = useState("");
  const [cBusy, setCBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/employees/${employee.employee_id}/benefits`);
      setData(data);
      setAllowance(data.allowance ? String(data.allowance) : "");
    } catch {
      toast.error("Could not load benefits");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.employee_id]);
  useEffect(() => { load(); }, [load]);

  const saveAllowance = async () => {
    setBusy(true);
    try {
      await api.put(`/employees/${employee.employee_id}/benefits`, { allowance: parseFloat(allowance) || 0 });
      toast.success("Allowance saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the allowance");
    } finally { setBusy(false); }
  };

  const addClaim = async () => {
    if (!cDate) { toast.error("Pick the service date"); return; }
    if (!cDesc.trim()) { toast.error("Add a description"); return; }
    if (!(parseFloat(cAmount) > 0)) { toast.error("Enter the amount"); return; }
    setCBusy(true);
    try {
      await api.post(`/employees/${employee.employee_id}/benefits/claim`, {
        service_date: cDate,
        description: cDesc.trim(),
        amount: parseFloat(cAmount),
        note: cNote.trim(),
      });
      setCDate(""); setCDesc(""); setCAmount(""); setCNote("");
      toast.success("Old claim recorded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the claim");
    } finally { setCBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3" data-testid={`${testid}-dialog`}>
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
              <HeartHandshake className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-base font-bold uppercase tracking-tight" data-testid={`${testid}-title`}>Benefits — {employee.name}</h3>
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">Health spending account · {data?.year || ""}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          {!data ? (
            <div className="flex items-center justify-center py-8 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <>
              {/* Allowance + balance */}
              <div className="grid grid-cols-3 gap-2" data-testid={`${testid}-summary`}>
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5 text-center">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">Allowance</p>
                  <p className="mt-0.5 text-sm font-bold text-white" data-testid={`${testid}-allowance-value`}>{data.allowance ? money(data.allowance) : "—"}</p>
                </div>
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5 text-center">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">Used</p>
                  <p className="mt-0.5 text-sm font-bold text-amber-600 dark:text-amber-300" data-testid={`${testid}-used`}>{money(data.used)}</p>
                </div>
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-2.5 text-center">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-white/45">Remaining</p>
                  <p className="mt-0.5 text-sm font-bold text-emerald-600 dark:text-emerald-300" data-testid={`${testid}-remaining`}>{data.remaining == null ? "—" : money(data.remaining)}</p>
                </div>
              </div>
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Annual allowance ($)</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={allowance}
                    onChange={(e) => setAllowance(e.target.value)}
                    placeholder="e.g. 1500.00"
                    data-testid={`${testid}-allowance-input`}
                    className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
                  />
                </div>
                <button
                  onClick={saveAllowance} disabled={busy}
                  data-testid={`${testid}-allowance-save`}
                  className="inline-flex h-10 items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 text-xs font-semibold text-white hover:bg-[#1633a3] disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save
                </button>
              </div>

              {/* Claims list */}
              <div>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/45">Claims this year ({data.claims.length})</p>
                {data.claims.length === 0 ? (
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid={`${testid}-claims-empty`}>No claims yet</p>
                ) : (
                  <ul className="space-y-1.5" data-testid={`${testid}-claims`}>
                    {data.claims.map((c) => (
                      <li key={c.claim_id} data-testid={`${testid}-claim-${c.claim_id}`} className="flex flex-wrap items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                        <span className="font-mono text-white/70">{fmtDay(c.service_date)}</span>
                        <span className="min-w-0 flex-1 truncate text-white/80">{c.description}</span>
                        {c.manual && <span className="rounded-full border border-white/20 bg-white/5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/50">old claim</span>}
                        <span className="font-mono font-semibold text-white">{money(c.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Record a legacy claim */}
              <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/45">Record an old claim (pre-app)</p>
                <div className="grid grid-cols-2 gap-2">
                  <input type="date" value={cDate} onChange={(e) => setCDate(e.target.value)} data-testid={`${testid}-legacy-date`}
                    className="h-9 w-full rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white focus:border-[#1a3fd1] focus:outline-none" />
                  <input type="number" min="0" step="0.01" value={cAmount} onChange={(e) => setCAmount(e.target.value)} placeholder="Amount ($)" data-testid={`${testid}-legacy-amount`}
                    className="h-9 w-full rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white focus:border-[#1a3fd1] focus:outline-none" />
                </div>
                <input value={cDesc} onChange={(e) => setCDesc(e.target.value)} placeholder="Description (e.g. Dental — fillings)" data-testid={`${testid}-legacy-desc`}
                  className="mt-2 h-9 w-full rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white focus:border-[#1a3fd1] focus:outline-none" />
                <input value={cNote} onChange={(e) => setCNote(e.target.value)} placeholder="Note (optional)" data-testid={`${testid}-legacy-note`}
                  className="mt-2 h-9 w-full rounded-md border border-white/15 bg-white/5 px-2 text-xs text-white focus:border-[#1a3fd1] focus:outline-none" />
                <button
                  onClick={addClaim} disabled={cBusy}
                  data-testid={`${testid}-legacy-submit`}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                >
                  {cBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add old claim
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BenefitsDialog;
